import _extends from "@babel/runtime/helpers/extends";
import { Box } from '@wix/design-system';
import { CoreDomainSearchStore, DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { DomainIntentReferral } from '@wix/domains-serverless-interface';
import React, { useEffect } from 'react';
import { ResultsHeader } from '../../../Components/Headers/ResultsHeader/ResultsHeader';
import { Recommendations, RecommendationsError } from '../../../Components/Recommendations';
import { VoucherBanner } from '../../../Components/VoucherBanner';
import { useDomainSearchGateBi } from '../../../hooks/useDomainSearchGateBi';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useQuerySubmit } from '../../../hooks/useQuerySubmit';
import { useRecommendations } from '../../../hooks/useRecommendations';
import { useResultsBack } from '../../../hooks/useResultsBack';
import { useResultsPageOfferingsBi } from '../../../hooks/useResultsPageOfferingsBi';
import { ExperimentSpecs } from '../../../constants';
import { DeclaredExperimentsProvider } from '../../../hooks/declaredExperiments';
import { usePerceptionEnabled, usePerceptionPricing, PERCEPTION_VOUCHER_BANNER } from '../../../pricingPerception';
import { ViewMode } from '../../../types';
import s from '../../resultsPage.scss';
import transition from '../../viewTransitions.css';
export const FunnelResultsPage = _ref => {
  let {
    handlers,
    externalViewer
  } = _ref;
  const {
    getQueryParam
  } = useQueryParams();
  const autoInvokeOverride = getQueryParam('invoke') === 'true' ? true : undefined;
  // Declared here rather than in the content component: the pricing call happens inside it, and a
  // provider below the caller yields an empty id list, which reads to the server as "declared
  // nothing" and silently returns ungated pricing.
  const experimentIds = usePerceptionEnabled() ? [ExperimentSpecs.PricingPerceptionFirstYear] : [];
  return /*#__PURE__*/React.createElement(DeclaredExperimentsProvider, {
    ids: experimentIds
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer: Boolean(externalViewer)
      },
      handlers,
      autoInvokeOverride,
      uiConfig: {
        fullPage: true,
        referralAdditionalInfo: DomainIntentReferral.CONNECT_FUNNEL,
        sameTab: true
      }
    }
  }, /*#__PURE__*/React.createElement(FunnelResultsPageContent, null)));
};
export const FunnelResultsPageContent = () => {
  const handleBackClick = useResultsBack({
    toGate: true
  });
  const {
    heroLayouts,
    ...perceptionPricing
  } = usePerceptionPricing('businessManagerResults');
  const voucherBannerConfigOverrides = perceptionPricing.Layouts ? PERCEPTION_VOUCHER_BANNER : undefined;
  useDomainSearchGateBi({
    viewMode: ViewMode.FUNNEL_RESULTS_PAGE
  });
  const {
    recommendations,
    isLoading,
    query,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetchingNextPage,
    error,
    biError,
    onRetry,
    isPricingLoading
  } = useRecommendations();
  const {
    updateUrl
  } = DomainSearchSuiteModule.SDK.hooks.useUrlRecovery();
  useQuerySubmit({});
  useResultsPageOfferingsBi({
    recommendations,
    isLoading,
    biError
  });
  useEffect(() => {
    return () => {
      CoreDomainSearchStore.resetStore();
    };
  }, []);
  const emptyQueryStatus = !isLoading && recommendations.length > 0 && !query;
  const searchBar = /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.DomainSearchBar, {
    compact: true,
    showOptionsIfEmptyInput: false,
    autoFocus: false,
    useModalToolbar: false,
    onSubmit: filters => {
      updateUrl(filters);
    },
    onFiltersChange: (key, value) => {
      if (key === 'tlds') {
        DomainSearchSuiteModule.internals.bi.onSelectTld == null || DomainSearchSuiteModule.internals.bi.onSelectTld({
          tld: JSON.stringify(value)
        });
      } else {
        DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
          ctaClick: key,
          value: JSON.stringify(value)
        });
      }
    },
    Layouts: {
      StatusComponent: () => emptyQueryStatus ? /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.EmptyQueryStatus, null) : /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.Status, null)
    }
  });
  return /*#__PURE__*/React.createElement(Box, {
    align: "center",
    display: "grid",
    className: `${s.resultsPage} ${s.noInsights}`,
    gap: "SP6 0",
    height: "100%",
    backgroundColor: "white",
    overflow: "auto"
  }, /*#__PURE__*/React.createElement(Box, {
    boxSizing: "border-box",
    align: "center",
    gridArea: "header",
    top: "0",
    position: "sticky",
    width: "100%",
    zIndex: 10,
    padding: "SP5 48px",
    backgroundColor: "white",
    direction: "vertical",
    gap: "SP6"
  }, /*#__PURE__*/React.createElement(VoucherBanner, {
    configOverrides: voucherBannerConfigOverrides
  }), /*#__PURE__*/React.createElement(ResultsHeader, {
    className: transition.header,
    onBackClick: handleBackClick
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      zIndex: 11
    },
    className: transition.searchBar
  }, searchBar)), error ? /*#__PURE__*/React.createElement(RecommendationsError, {
    error: error,
    onRetry: onRetry,
    gridColumn: "1 / -1"
  }) : /*#__PURE__*/React.createElement(Recommendations, _extends({
    recommendations: recommendations,
    isLoading: isLoading,
    hasMore: hasMore,
    isPricingLoading: isPricingLoading,
    fetchNextPage: () => {
      DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
        ctaClick: 'show_more_results'
      });
      fetchNextPage();
    },
    isFetchingNextPage: isFetchingNextPage,
    gridArea: "content",
    className: s.centeredContent
  }, perceptionPricing)));
};
//# sourceMappingURL=FunnelResultsPage.js.map